/**
 * Where to send a patient after they sign in or register — the patient-portal counterpart to
 * `web/src/auth/redirectTarget.ts`, restated here rather than reused directly because the "never
 * back to an auth page" rule has to name *this surface's own* login/register paths
 * (`/portal/:orgId/login`, `/portal/:orgId/register`), which are parameterized by `orgId` and so
 * cannot be a fixed constant the way the staff module's `AUTH_PATHS` is. Every other rule below is
 * unchanged from that module — see its own header for the full reasoning on each.
 */

const UNSAFE_CHARACTER_MAX_CODE_LOW = 0x20;
const UNSAFE_CHARACTER_MIN_CODE_HIGH = 0x7f;
const UNSAFE_CHARACTER_MAX_CODE_HIGH = 0x9f;

function hasUnsafeCharacter(value: string): boolean {
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    if (
      code <= UNSAFE_CHARACTER_MAX_CODE_LOW ||
      (code >= UNSAFE_CHARACTER_MIN_CODE_HIGH && code <= UNSAFE_CHARACTER_MAX_CODE_HIGH)
    ) {
      return true;
    }
  }
  return false;
}

function isSafePath(candidate: string, authPaths: readonly string[]): boolean {
  if (!candidate.startsWith('/')) return false;
  if (candidate.startsWith('//') || candidate.startsWith('/\\')) return false;
  if (hasUnsafeCharacter(candidate)) return false;
  if (candidate.includes(':')) return false;

  const [pathname] = candidate.split('?');
  if (pathname === undefined) return false;
  return !authPaths.includes(pathname);
}

export interface PortalRedirectCandidates {
  /** `location.state.from`, set by `RequirePatientSession`. Trusted first. */
  readonly fromState?: unknown;
  /** The raw `?next=` value, if any. Untrusted. */
  readonly fromQuery?: string | null;
}

/** Where a patient lands when there is no valid stored destination — this practice's own portal
 * home. */
export function defaultPortalRedirect(orgId: string): string {
  return `/portal/${orgId}`;
}

/** The first candidate that passes every rule, or this practice's own portal home. */
export function resolvePortalRedirectTarget(
  orgId: string,
  candidates: PortalRedirectCandidates,
): string {
  const { fromState, fromQuery } = candidates;
  const authPaths = [`/portal/${orgId}/login`, `/portal/${orgId}/register`];

  if (typeof fromState === 'string' && isSafePath(fromState, authPaths)) {
    return fromState;
  }
  if (typeof fromQuery === 'string' && isSafePath(fromQuery, authPaths)) {
    return fromQuery;
  }
  return defaultPortalRedirect(orgId);
}
